/* Brand Studio — existing Brand Studio MVP flow.
 *
 * A single, complete user flow the confectioner can walk through end to
 * end for the demo video:
 *   1. Land on Brand Studio preview page.
 *   2. Tap «Создать мой бренд» → 5-step wizard (name/mood/palette/shape/contacts).
 *   3. Tap «Создать варианты» → pick one of 4 deterministically-generated logos.
 *   4. «Сохранить мой бренд» → persistent Brand Profile screen.
 *   5. See «Твой бренд в работе» — Наклейка/Визитка/Карточка хранения previews
 *      auto-generated from the profile.
 *
 * NO real AI generation. Everything is SVG rendered deterministically from
 * the user's answers so the demo feels alive without paid infrastructure.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Palette, FileImage, Ticket, Sticker, Ribbon, Refrigerator,
  Truck, Scissors, Heart, Bookmark, Send, Layers, Layout, Paperclip,
  Stamp, Check, ChevronLeft, ChevronRight, X, Loader2, PenTool, Type,
  Shapes, Grid3X3, Sparkles, Camera, Package, Plus,
} from "lucide-react";
import { toast } from "sonner";
import BrandStudioShell from "@/components/brandStudio/BrandStudioShell";
import BrandStudioSignature from "@/components/brandStudio/BrandStudioSignature";
import { getLatestBrandStory, getVisualDirectionWorkspace, getVisualDNAWorkspace, saveBrandProfile } from "@/lib/brandStudioApi";
import { formatApiError } from "@/lib/api";
import { BRAND_CARD_STATUS, resolveBrandCard } from "@/lib/brandCardState.mjs";
import { BRAND_STUDIO_ROUTES, brandJourneyDestination } from "@/lib/brandJourney.mjs";
import {
  BRAND_STUDIO_CATEGORIES,
  BRAND_STUDIO_WORKSPACES,
} from "@/lib/brandStudioCatalog.mjs";

/* ─────────────────────────── static config ─────────────────────────── */

const BRAND_STUDIO_ICONS = {
  "file-image": FileImage,
  ticket: Ticket,
  bookmark: Bookmark,
  sticker: Sticker,
  ribbon: Ribbon,
  refrigerator: Refrigerator,
  truck: Truck,
  scissors: Scissors,
  heart: Heart,
  layers: Layers,
  send: Send,
  stamp: Stamp,
  layout: Layout,
  paperclip: Paperclip,
};

const MOODS = [
  { id: "premium",  label: "Премиальный",   accent: "#7A6247", serifTitle: true },
  { id: "minimal",  label: "Минималистичный", accent: "#2A1D14", serifTitle: false },
  { id: "gentle",   label: "Нежный",         accent: "#C68A93", serifTitle: true },
  { id: "classic",  label: "Классический",   accent: "#4A3728", serifTitle: true },
  { id: "modern",   label: "Современный",    accent: "#3D5A6C", serifTitle: false },
];

const PALETTES = [
  { id: "sand",    name: "Тёплый песок",      colors: ["#F7EFE0", "#EADFCF", "#C7A574", "#4A3728"] },
  { id: "cream",   name: "Кремовый румянец",  colors: ["#FBF5EA", "#F3DAD4", "#C68A93", "#6C4A50"] },
  { id: "sage",    name: "Пыльный шалфей",    colors: ["#F1EEE6", "#D8DCC6", "#8FA383", "#3E5344"] },
  { id: "cocoa",   name: "Тёмный какао",      colors: ["#F5EBD8", "#C9AC85", "#6E4B2E", "#231710"] },
  { id: "sky",     name: "Утреннее небо",     colors: ["#F4F1EC", "#DCE6EC", "#96B0BE", "#3D5A6C"] },
];

const LOGO_SHAPES = [
  { id: "monogram",    label: "Монограмма" },
  { id: "round",       label: "Круглый" },
  { id: "square",      label: "Квадратный" },
  { id: "horizontal",  label: "Горизонтальный" },
];

const HOME_VISUAL_DNA_MODULES = Object.freeze([
  { type: "logo-family", title: "Логотип и его версии", route: "logo-family", Icon: PenTool },
  { type: "color-system", title: "Цветовая система", route: "color-system", Icon: Palette },
  { type: "typography-system", title: "Типографика", route: "typography", Icon: Type },
  { type: "graphic-system", title: "Графический язык", route: "graphic-system", Icon: Shapes },
  { type: "patterns", title: "Фирменные мотивы / паттерны", route: "patterns", Icon: Grid3X3 },
  { type: "illustration-icon-language", title: "Иллюстрации и иконки", route: "illustration-icon-language", Icon: Sparkles },
  { type: "photography-direction", title: "Стиль фотографии", route: "photography-direction", Icon: Camera },
  { type: "materials-finishes", title: "Материалы и отделка", route: "materials-finishes", Icon: Layers },
  { type: "packaging-principles", title: "Принципы упаковки", route: "packaging-principles", Icon: Package },
]);

const VISUAL_DNA_STATUS_LABELS = Object.freeze({
  not_started: "Не начато",
  ready_to_create: "Готово к созданию",
  in_progress: "В работе",
  ready_for_review: "Готово к просмотру",
  approved: "Утверждено",
});

/* ─────────────────────────── page shell ─────────────────────────── */

export default function MOBrandStudio() {
  const nav = useNavigate();
  const [brandCardStatus, setBrandCardStatus] = useState(BRAND_CARD_STATUS.loading);
  const [journey, setJourney] = useState(null);
  const [brandStory, setBrandStory] = useState(null);
  const [visualDNAState, setVisualDNAState] = useState(null);

  const loadProfiles = async () => {
    setBrandCardStatus(BRAND_CARD_STATUS.loading);
    try {
      const [nextJourney, latestStory] = await Promise.all([
        getVisualDirectionWorkspace(),
        getLatestBrandStory(),
      ]);
      setJourney(nextJourney || null);
      setBrandStory(latestStory || null);
      setBrandCardStatus(BRAND_CARD_STATUS.resolved);
    } catch {
      setBrandCardStatus(BRAND_CARD_STATUS.error);
    }
  };

  useEffect(() => { loadProfiles(); }, []);
  useEffect(() => {
    let active = true;
    getVisualDNAWorkspace()
      .then((result) => { if (active) setVisualDNAState(result); })
      .catch(() => { if (active) setVisualDNAState(null); });
    return () => { active = false; };
  }, []);

  const brandCard = resolveBrandCard({ status: brandCardStatus, journey });

  return (
    <BrandStudioShell title={journey?.brand_name || "Brand Studio"}>
      <div data-testid="mo-brand-studio" className="pb-16 overflow-x-clip">
        <div className="grid min-w-0 items-start gap-6 xl:grid-cols-[minmax(0,896px)_minmax(360px,1fr)] xl:gap-8">
          <div className="min-w-0">

        {/* Approved responsive artwork with accessible HTML copy. */}
        <section className="relative mb-4 aspect-[1080/394] w-full max-w-full overflow-hidden md:aspect-[1536/561]"
                 data-testid="mo-brand-studio-hero">
          <picture className="absolute inset-0 block h-full w-full">
            <source media="(max-width: 767px)"
                    srcSet="/brand-studio/brand-studio-hero-mobile-1080x394.jpeg" />
            <img
              src="/brand-studio/brand-studio-hero-desktop-1536x561.jpeg"
              alt="Торт и фирменные материалы Brand Studio"
              width="1536"
              height="561"
              className="block h-full w-full object-contain"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              data-testid="mo-brand-studio-hero-image"
            />
          </picture>
          <div className="absolute inset-y-0 left-[5%] z-10 flex w-[39%] min-w-0 items-center md:left-[5.5%] md:w-[34%]"
               data-testid="mo-brand-studio-hero-copy">
            <div className="min-w-0 text-[#3F2B20]">
              <h1 className="max-w-full whitespace-nowrap text-[14px] font-medium uppercase leading-none tracking-[0.04em] text-[#5A382A] sm:text-[20px] sm:tracking-[0.1em] md:text-[32px] md:tracking-[0.12em] lg:text-[38px]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                BRAND STUDIO
              </h1>
              <div className="mt-1 text-[10.5px] font-normal leading-tight text-[#5B4326] sm:text-[12px] md:mt-1.5 md:text-[17px]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Твоя дизайн-мастерская
              </div>
              <p className="mt-1.5 text-[9px] leading-[1.2] text-[#6B5040] sm:text-[10px] md:mt-2.5 md:text-[12.5px] md:leading-[1.35]">
                <span className="block font-medium">Стиль — в каждой детали.</span>
                <span className="mt-0.5 block">Собери свой фирменный мир, который расскажет о тебе без слов.</span>
              </p>
            </div>
          </div>
        </section>

        {brandCard.kind === "loading" ? <BrandProfileLoadingSlot />
        : brandCard.kind === "error" ? <BrandProfileLoadError onRetry={loadProfiles} />
        : <BrandProfileView brandCard={brandCard} hasBrandStory={Boolean(brandStory)}
            onContinue={() => nav(brandCard.hasVisualDNA
              ? BRAND_STUDIO_ROUTES.visualDNA
              : brandJourneyDestination(brandCard.journey.state))} />}

        <section className="mb-4" data-testid="mo-brand-studio-categories">
          <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
            Что хочешь создать?
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {BRAND_STUDIO_CATEGORIES.map(({ id, title, route, icon }) => {
              const Icon = BRAND_STUDIO_ICONS[icon];
              return (
              <button key={id}
                   type="button"
                   onClick={() => nav(route)}
                   aria-label={`Открыть раздел «${title}»`}
                   className="min-w-0 min-h-11 rounded-xl bg-white p-3 flex items-center gap-2.5 text-left transition-colors hover:bg-[#FFFDF8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/40 focus-visible:ring-offset-2"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid={`mo-brand-studio-category-${id}`}>
                <div className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                     style={{ background: "#F5EBD8", color: "#8B6F4B" }}>
                  <Icon size={14} strokeWidth={1.7} />
                </div>
                <div className="min-w-0 text-[12.5px] leading-tight whitespace-normal" style={{ color: "#2A1D14" }}>
                  {title}
                </div>
              </button>
            );})}
          </div>
        </section>

        <section className="rounded-2xl bg-white p-4 md:p-5"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                 data-testid="mo-brand-studio-workshop">
          <div className="text-[10.5px] tracking-[0.18em] uppercase mb-3" style={{ color: "#8B6F4B" }}>
            Твоя мастерская
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
            {BRAND_STUDIO_WORKSPACES.map(({ id, title, shortDescription, route, icon }) => {
              const Icon = BRAND_STUDIO_ICONS[icon];
              return (
              <button key={id}
                   type="button"
                   onClick={() => nav(route)}
                   aria-label={`Открыть раздел «${title}»`}
                   className="min-w-0 min-h-11 rounded-xl p-3.5 text-left transition-colors hover:bg-[#F7EFE0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/40 focus-visible:ring-offset-2"
                   style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid={`mo-brand-studio-workspace-${id}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                       style={{ background: "#EADFCF", color: "#8B6F4B" }}>
                    <Icon size={14} strokeWidth={1.7} />
                  </div>
                  <div className="min-w-0 text-[13.5px] whitespace-normal" style={{ color: "#2A1D14" }}>{title}</div>
                </div>
                <div className="text-[11.5px] leading-relaxed" style={{ color: "#8B6F4B" }}>{shortDescription}</div>
              </button>
            );})}
          </div>
          <button type="button"
                  onClick={() => nav(BRAND_STUDIO_ROUTES.newVisualDirection)}
                  className="mt-3 flex min-h-11 w-full items-center gap-3 rounded-xl border border-[#D8C19E] bg-[#F5E9D8] px-3.5 py-3 text-left transition-colors hover:bg-[#EFE0CA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/50 xl:hidden"
                  data-testid="new-visual-direction-mobile-entry">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#E7D2B4] text-[#684831]" aria-hidden="true"><Plus size={15} /></span>
            <span className="min-w-0">
              <span className="block text-[13px] text-[#2A1D14]">Новый визуальный поиск</span>
              <span className="mt-0.5 block text-[10.5px] leading-relaxed text-[#806B52]">Референсы, анализ и новые направления</span>
            </span>
          </button>
        </section>
        <footer className="pt-10 pb-2">
          <BrandStudioSignature />
        </footer>
          </div>
          <VisualDNAHomeGrid state={visualDNAState} onOpen={nav} />
        </div>
      </div>

    </BrandStudioShell>
  );
}

function VisualDNAHomeGrid({ state, onOpen }) {
  return (
    <div className="hidden min-w-0 xl:block" data-testid="visual-dna-home-grid">
      <section className="rounded-2xl border border-[#DFCDB3] bg-[#FFFDF8] p-4">
        <div className="px-1 pb-3">
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#8B6F4B]">Визуальная ДНК</div>
          <p className="mt-1.5 text-[11.5px] leading-relaxed text-[#6D5842]">Быстрый доступ к системе бренда</p>
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={() => onOpen(BRAND_STUDIO_ROUTES.newVisualDirection)}
            className="group flex min-h-[132px] min-w-0 flex-col rounded-xl border border-[#CDB18B] bg-[#F0E0CA] p-3.5 text-left transition-colors hover:bg-[#EAD7BC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B99361]/50"
            data-testid="new-visual-direction-entry"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#FFF8EE] text-[#684831]" aria-hidden="true"><Plus size={17} /></span>
            <span className="mt-3 break-words text-[14px] leading-tight text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Создать новое визуальное направление</span>
            <span className="mt-auto pt-2 text-[9.5px] leading-snug text-[#72553D]">Референсы, анализ и новый поиск</span>
          </button>
          {HOME_VISUAL_DNA_MODULES.map((item) => {
            const module = state?.modules?.find((entry) => entry.module_type === item.type);
            const status = VISUAL_DNA_STATUS_LABELS[module?.status] || "Не начато";
            const { Icon } = item;
            return (
              <button
                key={item.type}
                type="button"
                onClick={() => onOpen(`${BRAND_STUDIO_ROUTES.visualDNA}/${item.route}`)}
                className="group flex min-h-[132px] min-w-0 flex-col rounded-xl border border-[#E8DCCA] bg-white p-3.5 text-left transition-colors hover:bg-[#FBF6ED] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/50"
                data-testid={`visual-dna-home-card-${item.type}`}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#F1E5D1] text-[#7A6247]" aria-hidden="true"><Icon size={16} /></span>
                <span className="mt-3 break-words text-[14px] leading-tight text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{item.title}</span>
                <span className="mt-auto pt-3 text-[8.5px] uppercase tracking-[0.09em] text-[#806B52]">{status}</span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/* ─────────────────────────── logo mocks ───────────────────────────
 * Deterministic SVG mocks so 4 variants feel like directions of one brand.
 * They read from the same brand_name + monogram + mood + palette. */

function fitLogoText(value, { maxChars = 28, large = 40, medium = 32, small = 25 } = {}) {
  const source = (value || "Твой бренд").trim() || "Твой бренд";
  const text = source.length > maxChars
    ? `${source.slice(0, maxChars - 1).trimEnd()}…`
    : source;
  const fontSize = text.length > 22 ? small : text.length > 15 ? medium : large;
  return { text, fontSize };
}

function LogoSVG({ variant, profile }) {
  const { brand_name, monogram, palette } = profile;
  const colors = palette?.colors || ["#F7EFE0", "#EADFCF", "#C7A574", "#4A3728"];
  const mood = MOODS.find((m) => m.id === profile.mood) || MOODS[0];
  const serifFamily = "'Cormorant Garamond', serif";
  const sansFamily = "'Inter', sans-serif";
  const family = mood.serifTitle ? serifFamily : sansFamily;
  const ink = colors[colors.length - 1];
  const soft = colors[0];
  const accent = colors[Math.max(0, colors.length - 2)];
  const initials = (monogram || (brand_name || "A").trim().split(/\s+/).map(w => w[0]).join("").slice(0, 3)).toUpperCase();
  const typographicName = fitLogoText(brand_name);
  const markName = fitLogoText(brand_name, { maxChars: 22, large: 28, medium: 24, small: 20 });

  if (variant === "typographic") {
    return (
      <svg width="100%" height="100%" className="block max-w-full max-h-full" viewBox={`0 0 400 240`} preserveAspectRatio="xMidYMid meet">
        <rect width="400" height="240" fill={soft} rx="8" />
        <text x="200" y="120" fontFamily={family} fontSize={typographicName.fontSize} letterSpacing="6" fill={ink}
              textAnchor="middle" dominantBaseline="middle">{typographicName.text}</text>
        <line x1="120" y1="150" x2="280" y2="150" stroke={accent} strokeWidth="1.4" />
        <text x="200" y="180" fontFamily={sansFamily} fontSize="10" letterSpacing="6" fill={accent}
              textAnchor="middle">{mood.label.toUpperCase()}</text>
      </svg>
    );
  }
  if (variant === "monogram") {
    return (
      <svg width="100%" height="100%" className="block max-w-full max-h-full" viewBox={`0 0 240 240`} preserveAspectRatio="xMidYMid meet">
        <rect width="240" height="240" fill={soft} rx="8" />
        <circle cx="120" cy="115" r="72" fill="none" stroke={accent} strokeWidth="2" />
        <text x="120" y="128" fontFamily={family} fontSize="72" fontWeight="500" fill={ink}
              textAnchor="middle" dominantBaseline="middle">{initials || "A"}</text>
        <text x="120" y="205" fontFamily={sansFamily} fontSize="8" letterSpacing="6" fill={accent}
              textAnchor="middle">{(brand_name || "").toUpperCase().slice(0,24)}</text>
      </svg>
    );
  }
  if (variant === "mark_and_name") {
    return (
      <svg width="100%" height="100%" className="block max-w-full max-h-full" viewBox={`0 0 400 200`} preserveAspectRatio="xMidYMid meet">
        <rect width="400" height="200" fill={soft} rx="8" />
        {/* petite mark */}
        <g transform="translate(80,100)">
          <circle r="40" fill={accent} />
          <text x="0" y="4" fontFamily={family} fontSize="30" fill={soft}
                textAnchor="middle" dominantBaseline="middle">{initials.slice(0,2)}</text>
        </g>
        <text x="150" y="95" fontFamily={family} fontSize={markName.fontSize} fill={ink}
              dominantBaseline="middle">{markName.text}</text>
        <text x="150" y="125" fontFamily={sansFamily} fontSize="9" letterSpacing="4" fill={accent}
              dominantBaseline="middle">{mood.label.toUpperCase()}</text>
      </svg>
    );
  }
  // compact — for sticker/stamp
  return (
    <svg width="100%" height="100%" className="block max-w-full max-h-full" viewBox={`0 0 200 200`} preserveAspectRatio="xMidYMid meet">
      <rect width="200" height="200" fill={soft} rx="8" />
      <circle cx="100" cy="100" r="80" fill="none" stroke={ink} strokeWidth="2" />
      <circle cx="100" cy="100" r="72" fill="none" stroke={accent} strokeWidth="0.7" strokeDasharray="2 3" />
      <text x="100" y="108" fontFamily={family} fontSize="46" fontWeight="500" fill={ink}
            textAnchor="middle" dominantBaseline="middle">{initials.slice(0,2)}</text>
    </svg>
  );
}

const LOGO_VARIANTS = [
  { id: "typographic",   label: "Типографический" },
  { id: "monogram",      label: "Монограмма" },
  { id: "mark_and_name", label: "Знак + название" },
  { id: "compact",       label: "Компактная (для наклейки)" },
];

function LogoPreviewFrame({ variant, profile }) {
  return (
    <div className="w-full aspect-[5/3] min-w-0 overflow-hidden p-3 grid place-items-center"
         data-testid="mo-brand-logo-preview-frame">
      <LogoSVG variant={variant} profile={profile} />
    </div>
  );
}

/* ─────────────────────────── Brand profile view ─────────────────────────── */

function BrandProfileLoadingSlot() {
  return (
    <section className="mb-4 min-h-[104px] w-full rounded-2xl bg-[#FFFDF8] px-3 py-2.5 md:min-h-[128px] md:px-5 md:py-4"
             style={{ boxShadow: "0 8px 24px rgba(74,55,40,0.04), inset 0 0 0 1px #E8D8C0" }}
             aria-busy="true" data-testid="mo-brand-studio-profile-loading">
      <span className="sr-only">Загружаем данные бренда</span>
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 md:gap-x-5 md:gap-y-2">
        <div className="min-w-0" aria-hidden="true">
          <div className="h-2 w-16 rounded-full bg-[#EDE2D1] md:h-2.5 md:w-20" />
          <div className="mt-1 h-5 w-32 max-w-[70%] rounded-md bg-[#F1E8DB] md:mt-2 md:h-7 md:w-44" />
        </div>
        <div className="h-[34px] w-28 max-w-full rounded-xl border border-[#E4D4BC] bg-[#FBF5EA] md:h-10 md:w-36" aria-hidden="true" />
        <div className="col-span-2 h-6 w-full max-w-xl rounded-md bg-[#F5EEE4] md:h-8" aria-hidden="true" />
      </div>
    </section>
  );
}

function BrandProfileLoadError({ onRetry }) {
  return (
    <section className="mb-4 min-h-[104px] w-full rounded-2xl bg-[#FFFDF8] px-3 py-2.5 md:min-h-[128px] md:px-5 md:py-4"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
             data-testid="mo-brand-studio-profile-error">
      <h2 className="text-[19px] leading-tight text-[#2A1D14] md:text-[22px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Не удалось загрузить данные бренда
      </h2>
      <p className="mt-1.5 text-[11px] leading-snug text-[#6F5942] md:mt-2 md:text-[12.5px] md:leading-relaxed">Попробуй ещё раз. Сохранённые данные не изменены.</p>
      <button type="button" onClick={onRetry}
              className="mt-2 h-[34px] rounded-xl border border-[#D9C39F] px-2.5 text-[10px] text-[#4A3728] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/40 md:mt-4 md:min-h-11 md:px-3 md:text-[12px]">
        Повторить
      </button>
    </section>
  );
}

function BrandProfileView({ brandCard, hasBrandStory = false, onContinue }) {
  const { brandName, hasBrandProfileV2, hasVisualDNA } = brandCard;

  return (
    <section className="mb-4 min-h-[104px] min-w-0 w-full rounded-2xl bg-[#FFFDF8] px-3 py-2.5 md:min-h-[128px] md:px-5 md:py-4"
             style={{ boxShadow: "0 8px 24px rgba(74,55,40,0.04), inset 0 0 0 1px #E8D8C0" }}
             data-testid="mo-brand-studio-profile">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 md:gap-x-5 md:gap-y-2">
        <div className="min-w-0">
          <div className="text-[9px] tracking-[0.2em] uppercase md:text-[10.5px] md:tracking-[0.24em]" style={{ color: "#8B6F4B" }}>
            ТВОЙ БРЕНД
          </div>
          <h2 className="mt-0.5 text-[18px] md:mt-1 md:text-[32px] leading-none break-words"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
              data-testid="mo-brand-studio-profile-name">
            {hasBrandProfileV2 ? brandName : hasBrandStory ? "Создание бренда" : "Создай свой бренд"}
          </h2>
        </div>
        <button type="button"
                  onClick={onContinue}
                  className="h-[34px] w-fit max-w-full px-2 rounded-xl text-[9.5px] sm:text-[10.5px] md:h-10 md:px-3 md:text-[12px] inline-flex items-center justify-center gap-1.5 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/40 focus-visible:ring-offset-2"
                  style={{ background: "#FBF5EA", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                  data-testid="mo-brand-studio-create-v2">
          <Palette size={13} className="hidden sm:block" /> {hasVisualDNA ? "Открыть мой бренд" : (hasBrandProfileV2 || hasBrandStory) ? "Продолжить создание" : "Начать создание"}
        </button>
        <p className="col-span-2 max-w-2xl text-[10px] leading-[1.2] text-[#5B4326] md:text-[12.5px] md:leading-[1.4]" data-testid="mo-brand-studio-journey-state">
          {hasVisualDNA
            ? "Визуальная система создаётся. Здесь собраны утверждённое направление и готовые элементы бренда."
            : hasBrandProfileV2
            ? "Собери визуальную ДНК своего бренда — от характера и направления до цвета, типографики и фирменных элементов."
            : hasBrandStory
            ? "Исходный рассказ подтверждён. Следующий этап — бренд-портрет."
            : "Расскажи о своём бренде — это станет основой визуальной системы."}
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────── Brand-in-action mockups ─────────────────────────── */

function BrandInAction({ profile }) {
  const c = profile.palette?.colors || ["#F7EFE0", "#EADFCF", "#C7A574", "#4A3728"];
  const soft = c[0], mid = c[1] || c[0], accent = c[Math.max(0, c.length-2)], ink = c[c.length-1];
  return (
    <section className="mb-6" data-testid="mo-brand-studio-in-action">
      <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
        Твой бренд в работе
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Sticker */}
        <div className="rounded-2xl p-3 grid place-items-center"
             style={{ background: mid, boxShadow: "inset 0 0 0 1px #EFE4D0", minHeight: 200 }}
             data-testid="mo-brand-studio-mock-sticker">
          <div className="rounded-full bg-[color:var(--soft)] w-40 h-40 grid place-items-center relative"
               style={{ background: soft, boxShadow: `inset 0 0 0 2px ${ink}` }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", color: ink, textAlign: "center" }}>
              <div className="text-[10px] tracking-[0.28em] uppercase mb-1" style={{ color: accent }}>Handmade</div>
              <div className="text-[20px] leading-tight">{profile.brand_name}</div>
              {profile.monogram && <div className="mt-1 text-[10px]" style={{ color: accent }}>{profile.monogram}</div>}
            </div>
          </div>
          <div className="mt-2 text-[10.5px] tracking-[0.16em] uppercase" style={{ color: ink }}>Наклейка</div>
        </div>
        {/* Business card */}
        <div className="rounded-2xl p-3 grid place-items-center"
             style={{ background: soft, boxShadow: "inset 0 0 0 1px #EFE4D0", minHeight: 200 }}
             data-testid="mo-brand-studio-mock-card">
          <div className="rounded-md w-full max-w-[260px] aspect-[1.7/1] px-4 py-3 flex flex-col justify-between"
               style={{ background: "#FFFFFF", boxShadow: `inset 0 0 0 1px ${accent}` }}>
            <div>
              <div className="text-[9px] tracking-[0.24em] uppercase" style={{ color: accent }}>ANOKINA</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", color: ink, fontSize: 20, lineHeight: 1.1, marginTop: 2 }}>
                {profile.brand_name}
              </div>
            </div>
            <div className="text-[9.5px] leading-snug" style={{ color: ink }}>
              {profile.contacts?.instagram && <div>{profile.contacts.instagram}</div>}
              {profile.contacts?.phone && <div>{profile.contacts.phone}</div>}
              {profile.contacts?.website && <div>{profile.contacts.website}</div>}
            </div>
          </div>
          <div className="mt-2 text-[10.5px] tracking-[0.16em] uppercase" style={{ color: ink }}>Визитка</div>
        </div>
        {/* Storage & serving guide (compact, brand-driven, contacts optional) */}
        <div className="rounded-2xl p-3 grid place-items-center"
             style={{ background: mid, boxShadow: "inset 0 0 0 1px #EFE4D0", minHeight: 200 }}
             data-testid="mo-brand-studio-mock-storage">
          <div className="rounded-md w-full max-w-[260px] p-3.5 flex flex-col gap-2.5"
               style={{ background: soft, boxShadow: `inset 0 0 0 1px ${accent}` }}>
            <div>
              <div className="text-[9px] tracking-[0.24em] uppercase truncate"
                   style={{ color: accent }}
                   data-testid="mo-brand-studio-mock-storage-brand">
                {profile.brand_name}
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", color: ink,
                            fontSize: 15, lineHeight: 1.1, marginTop: 3 }}>
                Гид по хранению и подаче
              </div>
              <div className="mt-1.5 h-px" style={{ background: accent, opacity: 0.5 }} />
            </div>

            <ul className="text-[10px] leading-snug space-y-1" style={{ color: ink }}
                data-testid="mo-brand-studio-mock-storage-tips">
              <li><span style={{ color: accent }}>·</span> Хранить при +2…+6 °C.</li>
              <li><span style={{ color: accent }}>·</span> Перевозить на ровной поверхности.</li>
              <li><span style={{ color: accent }}>·</span> Достать за 20–30 мин до подачи.</li>
              <li><span style={{ color: accent }}>·</span> Резать длинным сухим ножом.</li>
              <li><span style={{ color: accent }}>·</span> Свежий декор — перед подачей.</li>
            </ul>

            {(profile.contacts?.instagram || profile.contacts?.website || profile.contacts?.phone) && (
              <div className="pt-1.5 mt-0.5 text-[9px] leading-snug"
                   style={{ color: ink, borderTop: `1px solid ${accent}55` }}
                   data-testid="mo-brand-studio-mock-storage-contacts">
                {profile.contacts?.instagram && <div className="truncate">{profile.contacts.instagram}</div>}
                {profile.contacts?.website && <div className="truncate">{profile.contacts.website}</div>}
                {profile.contacts?.phone && <div className="truncate">{profile.contacts.phone}</div>}
              </div>
            )}
          </div>
          <div className="mt-2 text-[10.5px] tracking-[0.16em] uppercase" style={{ color: ink }}>Гид по хранению и подаче</div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Wizard ─────────────────────────── */

/* Wizard step frame — extracted OUTSIDE the wizard component. Previously
 * it was declared inline as `const StepShell = ({...}) => (...)` inside
 * BrandWizard, which meant every keystroke (state update → re-render)
 * produced a NEW component identity. React then unmounted + remounted the
 * subtree — inputs lost focus after every character. Extracting fixes it. */
function WizardStep({ step, title, children }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[0.24em] uppercase mb-1" style={{ color: "#8B6F4B" }}>
        Шаг {step} из 6
      </div>
      <h3 className="text-[22px] leading-tight mb-4"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function BrandWizard({ profile, onClose, onSaved }) {
  const isEdit = !!profile;
  const [step, setStep] = useState(1); // 1..5, 6=variants
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => ({
    brand_name: profile?.brand_name || "",
    monogram: profile?.monogram || "",
    mood: profile?.mood || MOODS[0].id,
    palette: profile?.palette || PALETTES[0],
    logo_shape: profile?.logo_shape || LOGO_SHAPES[0].id,
    contacts: {
      instagram: profile?.contacts?.instagram || "",
      phone: profile?.contacts?.phone || "",
      website: profile?.contacts?.website || "",
    },
    logo_variant: profile?.logo_variant || null,
  }));
  const [customPaletteOpen, setCustomPaletteOpen] = useState(false);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setContact = (k, v) => setDraft((d) => ({ ...d, contacts: { ...d.contacts, [k]: v } }));

  const canNext = useMemo(() => {
    if (step === 1) return (draft.brand_name || "").trim().length > 0;
    if (step === 2) return !!draft.mood;
    if (step === 3) return !!draft.palette;
    if (step === 4) return !!draft.logo_shape;
    return true;
  }, [step, draft]);

  const submit = async (variant) => {
    setSaving(true);
    try {
      const payload = {
        brand_name: draft.brand_name.trim(),
        monogram: (draft.monogram || "").trim() || null,
        mood: draft.mood,
        palette: draft.palette,
        logo_shape: draft.logo_shape,
        contacts: {
          instagram: draft.contacts.instagram || null,
          phone: draft.contacts.phone || null,
          website: draft.contacts.website || null,
        },
        logo_variant: variant,
      };
      const bp = await saveBrandProfile(payload);
      onSaved(bp);
    } catch (e) { toast.error(formatApiError(e) || "Не удалось сохранить бренд"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.55)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="mo-brand-wizard">
      <div className="w-full md:max-w-2xl bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "94dvh" }}>

        <div className="flex-shrink-0 p-5 md:p-6 pb-2 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10.5px] tracking-[0.28em] uppercase mb-1" style={{ color: "#8B6F4B" }}>ANOKINA STUDIO</div>
            <div className="text-[19px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              {isEdit ? "Редактировать бренд" : "Создать мой бренд"}
            </div>
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                  style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  aria-label="Закрыть"
                  data-testid="mo-brand-wizard-close">
            <X size={13} />
          </button>
        </div>

        <div className="flex-1 min-h-0 p-5 md:p-6 pt-2 overflow-y-auto">
          {step === 1 && (
            <WizardStep step={step} title="Как называется твой бренд?">
              <div className="space-y-3">
                <div>
                  <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Название бренда</div>
                  <input value={draft.brand_name}
                         onChange={(e) => setField("brand_name", e.target.value.slice(0, 80))}
                         placeholder="Например: Анна Иванова"
                         className="w-full h-11 px-3 rounded-md bg-white text-base outline-none"
                         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                         data-testid="mo-brand-wizard-name" />
                </div>
                <div>
                  <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Инициалы / монограмма (необязательно)</div>
                  <input value={draft.monogram}
                         onChange={(e) => setField("monogram", e.target.value.slice(0, 6))}
                         placeholder="АИ"
                         className="w-full h-11 px-3 rounded-md bg-white text-base outline-none tracking-[0.15em]"
                         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                         data-testid="mo-brand-wizard-monogram" />
                </div>
              </div>
            </WizardStep>
          )}

          {step === 2 && (
            <WizardStep step={step} title="Как должен ощущаться твой бренд?">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {MOODS.map((m) => (
                  <button key={m.id}
                          onClick={() => setField("mood", m.id)}
                          className="rounded-xl p-3.5 text-left transition"
                          style={{
                            background: draft.mood === m.id ? "#4A3728" : "#FFFFFF",
                            color: draft.mood === m.id ? "#F7EFE0" : "#2A1D14",
                            boxShadow: draft.mood === m.id
                              ? "0 6px 18px -12px rgba(74,55,40,0.55)"
                              : "inset 0 0 0 1px #EFE4D0",
                          }}
                          data-testid={`mo-brand-wizard-mood-${m.id}`}>
                    <div className="w-6 h-6 rounded-full mb-2" style={{ background: m.accent }} />
                    <div className="text-[13.5px]"
                         style={{ fontFamily: m.serifTitle ? "'Cormorant Garamond', serif" : "'Inter', sans-serif" }}>
                      {m.label}
                    </div>
                  </button>
                ))}
              </div>
            </WizardStep>
          )}

          {step === 3 && (
            <WizardStep step={step} title="Выбери настроение цвета">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {PALETTES.map((p) => (
                  <button key={p.id}
                          onClick={() => setField("palette", p)}
                          className="rounded-xl p-3 flex items-center gap-3 text-left transition"
                          style={{
                            background: draft.palette?.id === p.id ? "#4A3728" : "#FFFFFF",
                            color: draft.palette?.id === p.id ? "#F7EFE0" : "#2A1D14",
                            boxShadow: draft.palette?.id === p.id
                              ? "0 6px 18px -12px rgba(74,55,40,0.55)"
                              : "inset 0 0 0 1px #EFE4D0",
                          }}
                          data-testid={`mo-brand-wizard-palette-${p.id}`}>
                    <div className="flex gap-1 shrink-0">
                      {p.colors.map((c) => (
                        <span key={c} className="w-6 h-6 rounded-full"
                              style={{ background: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)" }} />
                      ))}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13.5px] leading-tight"
                           style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.name}</div>
                    </div>
                  </button>
                ))}
                <button onClick={() => setCustomPaletteOpen((v) => !v)}
                        className="rounded-xl p-3 text-left"
                        style={{ background: "#F7F1E7", color: "#4A3728",
                                 boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                        data-testid="mo-brand-wizard-custom-palette-toggle">
                  <div className="text-[13.5px]">Выбрать свои цвета</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "#8B6F4B" }}>4 оттенка — от светлого к тёмному.</div>
                </button>
              </div>
              {customPaletteOpen && (
                <div className="mt-3 rounded-xl p-3 bg-white"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                  <div className="grid grid-cols-4 gap-2">
                    {(draft.palette?.colors || PALETTES[0].colors).map((color, i) => (
                      <label key={i} className="flex flex-col items-center gap-1">
                        <input type="color" value={color}
                               onChange={(e) => {
                                 const next = [...(draft.palette?.colors || PALETTES[0].colors)];
                                 next[i] = e.target.value;
                                 setField("palette", { id: "custom", name: "Свои цвета", colors: next });
                               }}
                               className="w-10 h-10 rounded-xl border-0 cursor-pointer"
                               data-testid={`mo-brand-wizard-color-${i}`} />
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </WizardStep>
          )}

          {step === 4 && (
            <WizardStep step={step} title="Основа логотипа">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {LOGO_SHAPES.map((s) => (
                  <button key={s.id}
                          onClick={() => setField("logo_shape", s.id)}
                          className="rounded-xl p-3 text-center transition"
                          style={{
                            background: draft.logo_shape === s.id ? "#4A3728" : "#FFFFFF",
                            color: draft.logo_shape === s.id ? "#F7EFE0" : "#2A1D14",
                            boxShadow: draft.logo_shape === s.id
                              ? "0 6px 18px -12px rgba(74,55,40,0.55)"
                              : "inset 0 0 0 1px #EFE4D0",
                          }}
                          data-testid={`mo-brand-wizard-shape-${s.id}`}>
                    <div className="mx-auto mb-2 w-14 h-14 grid place-items-center"
                         style={{
                           background: draft.palette?.colors?.[0] || "#F7EFE0",
                           borderRadius: s.id === "round" ? "9999px" : s.id === "square" ? 4 : s.id === "horizontal" ? 4 : 4,
                           boxShadow: `inset 0 0 0 1.5px ${draft.palette?.colors?.[draft.palette.colors.length-1] || "#4A3728"}`,
                           width: s.id === "horizontal" ? 84 : 56,
                           height: s.id === "horizontal" ? 40 : 56,
                         }}>
                      <span style={{ fontFamily: "'Cormorant Garamond', serif",
                                     color: draft.palette?.colors?.[draft.palette.colors.length-1] || "#4A3728",
                                     fontSize: s.id === "horizontal" ? 18 : 22 }}>
                        {(draft.monogram || draft.brand_name || "A").slice(0, s.id === "horizontal" ? 4 : 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="text-[12.5px]">{s.label}</div>
                  </button>
                ))}
              </div>
            </WizardStep>
          )}

          {step === 5 && (
            <WizardStep step={step} title="Что указать на фирменных материалах?">
              <p className="text-[12.5px] leading-relaxed mb-3" style={{ color: "#5B4326" }}>
                Добавь контакты, которые хочешь использовать на визитках, карточках и других материалах.
              </p>
              <div className="space-y-3">
                <ContactRow icon="@" label="Соцсеть / мессенджер" value={draft.contacts.instagram}
                            onChange={(v) => setContact("instagram", v)}
                            placeholder="@name или ссылка"
                            testId="mo-brand-wizard-instagram" />
                <ContactRow icon="☎" label="Телефон" value={draft.contacts.phone}
                            onChange={(v) => setContact("phone", v)}
                            placeholder="+7 (000) 000-00-00"
                            testId="mo-brand-wizard-phone" />
                <ContactRow icon="🌐" label="Сайт" value={draft.contacts.website}
                            onChange={(v) => setContact("website", v)}
                            placeholder="anokina.studio"
                            testId="mo-brand-wizard-website" />
              </div>
            </WizardStep>
          )}

          {step === 6 && (
            <WizardStep step={step} title="Выбери вариант логотипа">
              <div className="text-[12.5px] mb-3" style={{ color: "#5B4326" }}>
                Выбери вариант, который ближе твоему бренду.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {LOGO_VARIANTS.map((v) => {
                  const isPicked = draft.logo_variant?.id === v.id;
                  return (
                    <div key={v.id}
                         className="rounded-xl p-3 flex flex-col gap-2"
                         style={{
                           background: "#FFFFFF",
                           boxShadow: isPicked
                             ? "inset 0 0 0 2px #4A3728"
                             : "inset 0 0 0 1px #EFE4D0",
                         }}
                         data-testid={`mo-brand-wizard-variant-${v.id}`}>
                      <div className="rounded-md overflow-hidden"
                           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                        <LogoPreviewFrame variant={v.id} profile={draft} />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[12.5px]" style={{ color: "#5B4326" }}>{v.label}</div>
                        <button onClick={() => setField("logo_variant", v)}
                                className="h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1"
                                style={{
                                  background: isPicked ? "#4A3728" : "#F7F1E7",
                                  color: isPicked ? "#F7EFE0" : "#4A3728",
                                  boxShadow: isPicked ? "none" : "inset 0 0 0 1px #EFE4D0",
                                }}
                                data-testid={`mo-brand-wizard-variant-pick-${v.id}`}>
                          {isPicked ? (<><Check size={11} /> Выбрано</>) : "Выбрать этот вариант"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </WizardStep>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex-shrink-0 p-5 md:p-6 pt-3 flex items-center justify-between border-t"
             style={{ borderColor: "#EFE4D0" }}>
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose()}
                  className="h-10 px-3 rounded-xl text-[12.5px] inline-flex items-center gap-1"
                  style={{ background: "transparent", color: "#4A3728",
                           boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-brand-wizard-prev">
            <ChevronLeft size={13} /> {step === 1 ? "Отмена" : "Назад"}
          </button>
          {step < 5 && (
            <button onClick={() => setStep(step + 1)} disabled={!canNext}
                    className="h-10 px-4 rounded-xl text-[12.5px] inline-flex items-center gap-1 disabled:opacity-50"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-brand-wizard-next">
              Далее <ChevronRight size={13} />
            </button>
          )}
          {step === 5 && (
            <button onClick={() => setStep(6)}
                    className="h-10 px-4 rounded-xl text-[12.5px] inline-flex items-center gap-1"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-brand-wizard-generate">
              Создать варианты <ChevronRight size={13} />
            </button>
          )}
          {step === 6 && (
            <button onClick={() => submit(draft.logo_variant)}
                    disabled={saving || !draft.logo_variant}
                    className="h-10 px-4 rounded-xl text-[12.5px] inline-flex items-center gap-1 disabled:opacity-60"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-brand-wizard-save">
              {saving ? (<><Loader2 size={13} className="animate-spin" /> Сохраняем…</>)
                     : (isEdit ? "Сохранить изменения" : "Сохранить мой бренд")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactRow({ icon, label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>{label}</div>
      <div className="flex items-stretch">
        <div className="w-11 grid place-items-center rounded-l-md text-[15px]"
             style={{ background: "#F7F1E7", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
          {icon}
        </div>
        <input value={value || ""} onChange={(e) => onChange(e.target.value.slice(0, 100))}
               placeholder={placeholder}
               className="flex-1 h-11 px-3 rounded-r-md bg-white text-base outline-none"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
               data-testid={testId} />
      </div>
    </div>
  );
}
